import { message } from "antd";
import { Category, ContentTopic } from "api/config/chalice-types";
import { genericError } from "fixtures/globalConstants";
import { useCategories } from "hooks/useCategoryCRUD";
import { useCallback, useState } from "react";
import { useAppDispatch, useAppSelector } from "store";
import { updateBusinessDetails } from "store/business/businessActions";
import { currentBusinessGetter } from "store/user/userSlice";
import { updateBusinessTopics } from "../services/businessService";
import {
  ensureAndMapNewCategories,
  matchExistingCategoryIds,
} from "../services/categoryService";
import { InitialTopicData } from "../utils/csvParsing";

// Type for progress updates - kept here as it's intrinsic to the hook's state
export interface ProgressUpdate {
  current: number;
  total: number;
  message: string;
}

export const useBulkAddTopics = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const dispatch = useAppDispatch();
  const currentBusiness = useAppSelector(currentBusinessGetter);
  // Still need useCategories for access to existing categories and the raw createCategory function
  const { categories, createCategory } = useCategories();

  // Removed matchExistingCategoryIds - now imported from categoryService

  // Removed ensureAndMapNewCategories - now imported from categoryService

  // This function maps the newly created category IDs back to the topics list
  // It remains in the hook as it depends on the `newCategoryMap` generated
  // within the hook's process flow.
  const mapNewCategoryIds = useCallback(
    (
      topicsInfo: InitialTopicData[],
      newCategoryMap: Map<string, string>
    ): InitialTopicData[] => {
      return topicsInfo.map((item) => {
        // If categoryId already exists (was matched earlier), keep it
        if (item.categoryId && typeof item.categoryId === "string") {
          return item;
        }
        // Otherwise, try to find the ID in the newly created map
        const newId = newCategoryMap.get(item.categoryName.toLowerCase());
        if (newId) {
          // If found, update the item with the new categoryId
          return { ...item, categoryId: newId };
        }
        // If not found (e.g., creation failed), return the item as is (categoryId remains null)
        return item;
      });
    },
    []
  );

  // This function transforms the final InitialTopicData into ContentTopic format
  // suitable for the API/store update. It remains in the hook as it prepares
  // data specifically for the business update action.
  const buildNewContentTopics = useCallback(
    (finalTopicsInfo: InitialTopicData[]): ContentTopic[] => {
      const newContentTopics: ContentTopic[] = [];
      let skippedCount = 0;
      finalTopicsInfo.forEach((data) => {
        // Only include topics that have a valid categoryId after all matching/creation steps
        if (data.categoryId && typeof data.categoryId === "string") {
          newContentTopics.push({
            title: data.topicName,
            body: data.description,
            category_id: data.categoryId,
            enabled: true, // Default to enabled
          });
        } else {
          // Log an error and count skipped topics if categoryId is missing
          console.error(
            `Skipping topic "${data.topicName}" because category ID for "${data.categoryName}" was not found or creation failed.`
          );
          skippedCount++;
        }
      });
      // Show a warning if any topics were skipped
      if (skippedCount > 0) {
        message.warning(
          `${skippedCount} topic(s) were skipped due to issues resolving their categories.`
        );
      }
      return newContentTopics;
    },
    []
  );

  // Removed dispatchBusinessUpdate - logic moved to businessService.updateBusinessTopics
  // Prepare the dispatch function specifically for updating topics
  const dispatchTopicUpdate = useCallback(
    (payload: { topics: ContentTopic[] }) => {
      // We cast to `any` here because `updateBusinessDetails` might expect a broader
      // Business object, but we only care about updating topics in this context.
      // Consider creating a more specific action if possible.
      return dispatch(updateBusinessDetails(payload as any)).unwrap();
    },
    [dispatch]
  );

  const processAndAddTopics = useCallback(
    async (
      initialTopics: InitialTopicData[],
      onProgress: (update: ProgressUpdate) => void
    ) => {
      if (!initialTopics || initialTopics.length === 0) {
        message.info("No valid topics provided to add.");
        return { success: false };
      }

      // Duplicate check logic remains here
      const existingTopicNamesLower = new Set(
        (currentBusiness?.topics ?? []).map((t) => t.title.toLowerCase().trim())
      );
      const uniqueInitialTopics = initialTopics.filter((topic) => {
        const initialNameLower = topic.topicName.toLowerCase().trim();
        return !existingTopicNamesLower.has(initialNameLower);
      });
      const skippedCount = initialTopics.length - uniqueInitialTopics.length;
      if (skippedCount > 0) {
        message.warning(
          `${skippedCount} topic(s) were skipped because their names already exist.`
        );
      }
      if (uniqueInitialTopics.length === 0) {
        message.info(
          "No new unique topics found to add." +
            (skippedCount > 0 ? " All provided topics were duplicates." : "")
        );
        return { success: false };
      }

      setIsProcessing(true);
      try {
        // Step 1: Match against existing categories (using service)
        const matchedData = matchExistingCategoryIds(
          uniqueInitialTopics,
          categories
        );

        // Wrap the createCategory mutation call in a function that returns a promise
        const createCategoryPromise = (params: {
          name: string;
          description: string;
        }): Promise<Category> => {
          return new Promise((resolve, reject) => {
            createCategory(params, {
              onSuccess: (data) => resolve(data),
              onError: (error) => reject(error),
            });
          });
        };

        // Step 2: Ensure new categories exist and get their IDs (using service)
        // Pass the promise-wrapped function
        const { newCategoryMap, errorsOccurred: creationErrors } =
          await ensureAndMapNewCategories(
            matchedData,
            currentBusiness?.id,
            createCategoryPromise, // Use the wrapped function
            onProgress
          );

        // Step 3: Map the newly created category IDs back to the topic list (using hook's function)
        const finalMappedData = mapNewCategoryIds(matchedData, newCategoryMap);

        // Step 4: Build the ContentTopic objects for the API/store (using hook's function)
        const newContentTopics = buildNewContentTopics(finalMappedData);

        // Step 5: Dispatch the update to the business store (using service)
        await updateBusinessTopics(
          currentBusiness?.topics ?? [], // Provide existing topics
          newContentTopics, // Provide new topics
          dispatchTopicUpdate, // Provide the prepared dispatch function
          creationErrors // Pass the flag indicating if category errors occurred
        );

        return { success: true, topicsAdded: newContentTopics.length };
      } catch (error: any) {
        // Generic error handling for the overall process
        message.error(
          `An error occurred during topic processing: ${error.message || "Unknown error"}`
        );
        // Use the imported genericError handler
        genericError(error);
        return { success: false };
      } finally {
        setIsProcessing(false);
      }
    },
    [
      categories, // Dependency for matchExistingCategoryIds
      currentBusiness, // Dependency for businessId and existing topics
      createCategory, // Dependency for ensureAndMapNewCategories
      mapNewCategoryIds, // Local hook function dependency
      buildNewContentTopics, // Local hook function dependency
      dispatchTopicUpdate, // Prepared dispatch function dependency
    ]
  );

  return {
    isProcessing,
    processAndAddTopics,
    // Expose setIsProcessing if needed externally, otherwise might remove
    setIsProcessing,
  };
};
